import React, { useCallback } from 'react'
import { Box, Button } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import type { NumericAxis, DateTimeNumericAxis } from 'scichart'

// Import our refactored components and hooks
import { ChartContainer } from './ChartContainer/ChartContainer'
import { useChartSync } from './Hooks/useChartSync'
import { useChartDragDrop } from './Hooks/useChartDragDrop'

import type { WaveChart } from '../../../interfaces/waveChart'

/**
 * Props for the WaveChartCanvas component - Simplified Interface
 * Focuses on essential data and specific user actions that require parent handling
 */
export interface WaveChartCanvasProps {
  /** Array of charts with data and configuration */
  charts: WaveChart[]
  
  /** Enable X-axis synchronization between charts */
  enableXAxisSync?: boolean
  
  /** Enable mouse wheel zoom on charts */
  enableMouseWheelZoom?: boolean
  
  /** Legend corner for all charts (optional preset) */
  legendCorner?: 'tl' | 'tr' | 'bl' | 'br'
  /** Called when user changes legend corner for a chart */
  onLegendCornerChange?: (chartId: string, corner: 'tl' | 'tr' | 'bl' | 'br' | 'none') => void
  
  /** Called when user wants to add a new chart */
  onAddChart?: () => void
  
  /** Called when user clicks chart close button (parent removes from charts array) */
  onChartClose?: (chartId: string) => void
  
  /** Called when user changes chart height (for template modification tracking) */
  onChartHeightChange?: (chartId: string, height: number) => void
  /** Called when user clicks a legend item to open curve properties */
  onLegendItemClick?: (chartId: string, curveId: string) => void
  /** Called when user toggles curve visibility via legend */
  onLegendToggleVisibility?: (chartId: string, curveId: string) => void
  /** Called when user changes legend mode for a chart */
  onLegendModeChange?: (chartId: string, mode: 'overlay' | 'bottom' | 'none') => void
  
  /** Standard React props */
  className?: string
  style?: React.CSSProperties
}

/**
 * WaveChartCanvas - Simplified chart orchestrator
 * 
 * Focuses on:
 * - Rendering multiple charts using ChartContainer components
 * - Coordinating X-axis synchronization between charts
 * - Managing global drag-and-drop state for curve additions
 * - Handling chart-level operations (close, height changes)
 * - Self-contained with Add Chart functionality
 */
export const WaveChartCanvas: React.FC<WaveChartCanvasProps> = ({
  charts = [],
  enableXAxisSync = true,
  enableMouseWheelZoom = false,
  legendCorner = 'tr',
  onLegendCornerChange,
  onAddChart,
  onChartClose,
  onChartHeightChange,
  onLegendItemClick,
  onLegendToggleVisibility,
  onLegendModeChange,
  className,
  style
}) => {
  const handleDragOverChange = useCallback((_chartId: string | null) => {
    // Internal drag over state management - no external callback needed for now
  }, [])

  // X-axis synchronization management
  const {
    registerChart,
    unregisterChart,
    setEnabled: setSyncEnabled
  } = useChartSync()

  // Global drag-and-drop state management
  const {
    isChartDraggedOver
  } = useChartDragDrop()

  // Update sync enabled state when prop changes
  React.useEffect(() => {
    setSyncEnabled(enableXAxisSync)
  }, [enableXAxisSync, setSyncEnabled])

  return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%'
        }}
      >
        {charts.map((chart, index) => (
          <ChartContainer
            key={chart.id || `chart-${index}`}
            chartId={chart.id || `chart-${index}`}
            chart={chart}
            heightPreset={chart.heightPreset}
            isDraggedOver={isChartDraggedOver(chart.id || `chart-${index}`)}
            enableMouseWheelZoom={enableMouseWheelZoom}
            legendCorner={chart.legendCorner ?? legendCorner}
            onLegendCornerChange={onLegendCornerChange}
            onClose={onChartClose}
            onHeightChange={onChartHeightChange}
            onDragOverChange={handleDragOverChange}
            onLegendItemClick={onLegendItemClick}
            onLegendToggleVisibility={onLegendToggleVisibility}
            onLegendModeChange={onLegendModeChange}
            onSurfaceReady={(chartId, surface) => {
              // Register chart for X-axis sync
              if (enableXAxisSync && surface.xAxes.size() > 0) {
                registerChart({
                  chartId,
                  xAxis: surface.xAxes.get(0) as NumericAxis | DateTimeNumericAxis
                })
              }
            }}
            onSurfaceDestroyed={(chartId) => {
              // Unregister chart from sync
              unregisterChart(chartId)
            }}
          />
        ))}

        {/* Add Chart Button */}
        {onAddChart && (
          <Box sx={{ mt: charts.length > 0 ? 1 : 0, mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddChart}
              sx={{ 
                minWidth: 120,
                borderStyle: 'dashed',
                borderWidth: 2,
                '&:hover': {
                  borderStyle: 'dashed',
                  borderWidth: 2
                }
              }}
            >
              Add Chart
            </Button>
          </Box>
        )}

        {/* Empty State */}
        {charts.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'grey.500',
              borderRadius: 1,
              color: 'text.secondary'
            }}
          >
            No charts to display. Add charts to get started.
          </Box>
        )}
      </Box>
  )
} 
